import DataMap from 'assets/components/dataMap/DataMap';
import { useModalForm } from 'assets/components/form/modalForm/ModalForm';
import Icon from 'assets/components/icon/Icon';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import ErrorWrapper from 'assets/components/inputs/wrappers/ErrorWrapper';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { formatNumberWithSeparator } from 'assets/utils/data/Number';
import { groupBy, isEmpty, keys, sumBy } from 'lodash';
import ClaimJob from 'models/claims/claimJob/Model.ClaimJob';
import ClaimJobItemPayee from 'models/claims/claimJobItemPayee/Model.ClaimJobItemPayee';
import { CountryCode } from 'models/enums/CountryCode';
import LineItem from 'models/enums/LineItem';
import { useClaimEditJobPayeeItemCore, useClaimJobEdit } from '../../ClaimEdit.Job.Hooks';
import { ClaimJobForm } from '../ClaimEdit.Job.Form';

const ClaimEditJobFormItems = () => {
  const { form } = useModalForm<ClaimJobForm>();
  const { lang } = getLocales();
  const { getJobItemDefaultValues, isSamePayee, product, contract, isEditingTax, setIsEditingTax } = useClaimJobEdit();
  const { setOffset, setSearch, limit, offset, paginatedPayees, searchedPayees, isLoading } =
    useClaimEditJobPayeeItemCore({
      countryCode: CountryCode.CAN,
    });

  const updateValue = (fieldName: keyof Model.IClaimJobItem, value: any, templateId: number) => {
    const items = [...(form.data.claimJobItems ?? [])];

    const globalIndex = items.findIndex((item) => item.templateId === templateId);
    if (globalIndex === -1) return;

    const item = items[globalIndex];

    if (fieldName === 'claimJobItemPayee') {
      const newPayee = value as ClaimJobItemPayee;
      const provinceCode =
        newPayee?.payee?.provinceCode ?? newPayee?.dealer?.provinceCode ?? newPayee?.client?.provinceCode;

      const tax = product.productTaxes?.find((it) => it.provinceCode === provinceCode);

      const paymentMethodCode =
        form.data?.claimJobItems?.find(
          (jobItem) => isSamePayee(newPayee, jobItem.claimJobItemPayee) && jobItem.paymentMethodCode
        )?.paymentMethodCode ??
        newPayee?.dealer?.paymentMethodCode ??
        newPayee?.payee?.paymentMethodCode;

      item.gstHst = tax?.claimsGstHst ?? 0.0;
      item.provincialSalesTax = tax?.claimsProvincialSalesTax ?? 0.0;
      item.labourRate = 0.0;
      item.hoursWorked = item.jobItemTypeCode === LineItem.labour && !item.hoursWorked ? 1 : item.hoursWorked;

      if (item.jobItemTypeCode === LineItem.deductible) {
        item.correction = contract?.deductible ?? 0.0;
        item.adjudication = item.correction ?? 0.0;
        item.gstHst = 0.0;
        item.provincialSalesTax = 0.0;
      } else if (item.jobItemTypeCode === LineItem.labour) {
        item.labourRate = newPayee?.payee?.labourRate ?? newPayee?.dealer?.labourRate;
      }

      item.claimJobItemPayee = newPayee;
      item.paymentMethodCode = paymentMethodCode;
    }

    if (fieldName === 'lossCode' && item.jobItemTypeCode === LineItem.part)
      item.quantity = !item.quantity ? 1 : item.quantity;

    const corr = fieldName === 'correction' ? value : (item.correction ?? 0.0);
    const gst = fieldName === 'gstHst' ? value : (item.gstHst ?? 0.0);
    const pst = fieldName === 'provincialSalesTax' ? value : (item.provincialSalesTax ?? 0.0);

    items[globalIndex] = {
      ...item,
      [fieldName]: value,
      correction: corr,
      adjudication: corr,
      total: Number((corr + corr * (gst + pst)).toFixed(2)),
    };

    if (items[globalIndex].jobItemTypeCode === LineItem.deductible) {
      items[globalIndex].gstHst = 0.0;
      items[globalIndex].provincialSalesTax = 0.0;
      items[globalIndex].labourRate = 0.0;
      items[globalIndex].total = corr;
    }

    if (
      items[globalIndex].jobItemTypeCode === LineItem.part &&
      (fieldName === 'baseCost' || fieldName === 'quantity')
    ) {
      const quantity = Number(items[globalIndex].quantity ?? 0);
      const baseCost = Number(items[globalIndex].baseCost ?? 0);

      if (quantity === 0) {
        items[globalIndex].quantity = 0;
        items[globalIndex].baseCost = 0;
        items[globalIndex].correction = 0;
        items[globalIndex].adjudication = 0;
        items[globalIndex].total = 0;
      } else {
        const correction = baseCost * quantity;
        items[globalIndex].correction = correction;
        items[globalIndex].adjudication = correction;
        items[globalIndex].total = Number((correction + correction * (gst + pst)).toFixed(2));
      }
    }

    form.merge({ claimJobItems: items });
  };

  if (isEmpty(form.data.claimJobItems)) return <ErrorWrapper children={lang.noInfo} />;
  return (
    <FormWrapper>
      {keys(groupBy(form.data?.claimJobItems, 'jobItemTypeCode')).map((code) => {
        const group = groupBy(form.data?.claimJobItems, 'jobItemTypeCode')[code];
        return (
          <div key={code} style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ marginLeft: Theme.Size.XL, marginRight: Theme.Size.XL }}>
              <h4>{code}</h4>
            </div>
            <table className={ThemeTableSimple} style={{ fontSize: Theme.FontSize.S }}>
              <thead>
                <tr>
                  <th style={{ padding: '10px', width: '20px' }}>#</th>
                  <th>{lang.payee}</th>
                  {code !== LineItem.deductible && <th>{lang.lossCode}</th>}
                  {code === LineItem.part && (
                    <>
                      <th style={{ textAlign: 'center' }}>{lang.baseCost}</th>
                      <th style={{ textAlign: 'center' }}>{lang.quantity}</th>
                    </>
                  )}
                  <th>{lang.requestedAmount}</th>
                  {code !== LineItem.deductible && (
                    <>
                      <th style={{ textAlign: 'center' }}>{lang.gstHst}</th>
                      <th style={{ textAlign: 'center' }}>{lang.pst}</th>
                    </>
                  )}
                  <th>{lang.total}</th>
                </tr>
              </thead>
              <tbody>
                <DataMap
                  data={group}
                  render={({ data: item, index: itemIndex }) => {
                    const payee = new ClaimJobItemPayee(item?.claimJobItemPayee);
                    const payeeId = payee?.payee?.id ?? payee?.client?.id ?? payee?.dealer?.id;
                    const selectInputValue = payeeId ? `${payeeId}-${payee?.payeeType?.id}` : undefined;

                    return (
                      <tr key={itemIndex + item.currency}>
                        <td style={{ width: '20px', padding: '10px' }}>{itemIndex + 1}</td>
                        <td style={{ width: '15%' }}>
                          <SelectInput
                            name="claimJobItemPayee"
                            data={paginatedPayees?.map((it) => ({
                              ...it.displayInfo,
                              id: `${it.displayInfo.id}-${it.payeeType?.id}`,
                            }))}
                            displayText={payee?.displayInfo?.title}
                            value={selectInputValue}
                            limit={limit}
                            offset={offset}
                            count={searchedPayees?.length}
                            animateOut={() => {
                              setOffset(0);
                            }}
                            error={!item?.claimJobItemPayee && lang.required}
                            isLoading={isLoading}
                            onSearch={setSearch}
                            onOffsetChange={({ value: offset }) => setOffset(offset)}
                            onChange={({ claimJobItemPayee: id }) => {
                              const itemPayee = paginatedPayees?.find(
                                (it) => `${it.payee?.id ?? it.dealer?.id ?? it.client?.id}-${it.payeeType?.id}` === id
                              );

                              updateValue(
                                'claimJobItemPayee',
                                {
                                  ...itemPayee,
                                  ...getJobItemDefaultValues({
                                    jobItemTypeCode: item?.jobItemTypeCode,
                                    claimJobItemPayee: itemPayee,
                                  }),
                                },
                                item?.templateId
                              );

                              setOffset(0);
                              setSearch(undefined);
                            }}
                            htmlElementProps={{ title: payee?.displayInfo?.title }}
                            style={{
                              input: {
                                fontSize: Theme.FontSize.S,
                              },
                            }}
                          />
                        </td>
                        {item.jobItemTypeCode !== LineItem.deductible && (
                          <td style={{ width: '15%' }}>
                            <SelectInput
                              name="lossCode"
                              data={item?.lossCodes?.map((it) => ({
                                id: it.code,
                                title: it.descnEn,
                              }))}
                              value={item?.lossCode}
                              disabled={item?.lossCodes?.length === 1}
                              onChange={({ lossCode }) => updateValue('lossCode', lossCode, item?.templateId)}
                              error={!item?.lossCode && lang.required}
                              htmlElementProps={{
                                title: item.lossCodes?.find((it) => it.code === item?.lossCode)?.descnEn,
                              }}
                            />
                          </td>
                        )}
                        {item.jobItemTypeCode === LineItem.part && (
                          <>
                            <td>
                              <NumberInput
                                name="baseCost"
                                value={Number(item?.baseCost?.toFixed(2))}
                                onChange={({ baseCost }) => updateValue('baseCost', baseCost, item?.templateId)}
                                step={0.01}
                                style={{ input: { textAlign: 'right' } }}
                                iconBefore={<Icon class="fas-dollar-sign" />}
                              />
                            </td>

                            <td>
                              <NumberInput
                                name="quantity"
                                value={Number(item?.quantity)}
                                onChange={({ quantity }) => updateValue('quantity', quantity, item?.templateId)}
                                step={1}
                                style={{ input: { textAlign: 'center' } }}
                              />
                            </td>
                          </>
                        )}

                        <td>
                          <NumberInput
                            name="correction"
                            value={Number(item?.correction?.toFixed(2) ?? 0)}
                            onChange={({ correction }) => updateValue('correction', correction, item?.templateId)}
                            step={0.01}
                            style={{ input: { textAlign: 'right' } }}
                            iconBefore={<Icon class="fas-dollar-sign" />}
                            readOnly={
                              item.jobItemTypeCode === LineItem.deductible || item.jobItemTypeCode === LineItem.part
                            }
                          />
                        </td>
                        {item.jobItemTypeCode !== LineItem.deductible && (
                          <>
                            <td>
                              <NumberInput
                                name="gstHst"
                                value={Number(((item?.gstHst || 0.0) * item?.adjudication)?.toFixed(2))}
                                onChange={({ gstHst }) =>
                                  updateValue('gstHst', gstHst / item?.adjudication, item?.templateId)
                                }
                                onClick={() => {
                                  setIsEditingTax(true);
                                }}
                                step={0.01}
                                readOnly={!isEditingTax}
                                htmlElementProps={{ title: !isEditingTax ? lang.clickToEdit : undefined }}
                                error={
                                  item?.gstHst > 1 &&
                                  lang.mustBeEqualOrLessThanAmount.replace(
                                    '{amount}',
                                    `${lang.requestedAmount}: ${item?.adjudication?.toFixed(2)}`
                                  )
                                }
                                style={{ input: { textAlign: 'right', cursor: 'text' }, cursor: 'text' }}
                                iconBefore={<Icon class="fas-dollar-sign" />}
                              />
                            </td>
                            <td>
                              <NumberInput
                                name="provincialSalesTax"
                                value={Number(((item?.provincialSalesTax || 0.0) * item?.adjudication)?.toFixed(2))}
                                onChange={({ provincialSalesTax }) =>
                                  updateValue(
                                    'provincialSalesTax',
                                    provincialSalesTax / item?.adjudication,
                                    item?.templateId
                                  )
                                }
                                onClick={() => {
                                  setIsEditingTax(true);
                                }}
                                step={0.01}
                                readOnly={!isEditingTax}
                                htmlElementProps={{ title: !isEditingTax ? lang.clickToEdit : undefined }}
                                error={
                                  item?.provincialSalesTax > 1 &&
                                  lang.mustBeEqualOrLessThanAmount.replace(
                                    '{amount}',
                                    `${lang.requestedAmount}: ${item?.adjudication?.toFixed(2)}`
                                  )
                                }
                                style={{ input: { textAlign: 'right', cursor: 'text' }, cursor: 'text' }}
                                iconBefore={<Icon class="fas-dollar-sign" />}
                              />
                            </td>
                          </>
                        )}
                        <td style={{ width: '15%', padding: '0 12px 0 0' }}>
                          <NumberInput
                            name="total"
                            value={Number(item?.total?.toFixed(2) ?? 0)}
                            readOnly
                            onChange={({ total }) => updateValue('total', total, item?.templateId)}
                            style={{ input: { textAlign: 'right' } }}
                            iconBefore={<Icon class="fas-dollar-sign" />}
                          />
                        </td>
                      </tr>
                    );
                  }}
                />
              </tbody>
            </table>
          </div>
        );
      })}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <strong>
            {lang.totalAmountWithoutTaxes}:{' '}
            {formatNumberWithSeparator(sumBy(ClaimJob.aggregates(form.data as ClaimJob), (it) => it.ttl).toFixed(2))}{' '}
            CAD
          </strong>
          <strong>
            {lang.totalAmountWithTaxes}:{' '}
            {formatNumberWithSeparator(sumBy(ClaimJob.aggregates(form.data as ClaimJob), (it) => it.total).toFixed(2))}{' '}
            CAD
          </strong>
        </div>
      </div>
    </FormWrapper>
  );
};

export default ClaimEditJobFormItems;
